import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Package, ChevronDown, ChevronRight, Trash2, MapPin, QrCode, StickyNote, Search } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const itemLabel = (it) =>
  `${it.item_name}${it.size ? ` ${it.size}` : ''}${it.quantity > 1 ? ` ×${it.quantity}` : ''}`;

function AddItemsDialog({ employee, itemTypes, onAdded, canDeleteTypes }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [customs, setCustoms] = useState([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const allItems = [
    ...itemTypes,
    ...customs.filter((c) => !itemTypes.some((t) => t.toLowerCase() === c.toLowerCase())),
  ];
  const count = Object.keys(sel).length;

  const toggle = (name) => setSel((s) => {
    const n = { ...s };
    if (n[name]) delete n[name]; else n[name] = { size: 'none', qty: 1 };
    return n;
  });

  const setField = (name, field, value) =>
    setSel((s) => ({ ...s, [name]: { ...s[name], [field]: value } }));

  const addCustom = async () => {
    const n = newName.trim();
    if (!n) return;
    if (!allItems.some((i) => i.toLowerCase() === n.toLowerCase())) setCustoms((c) => [...c, n]);
    setSel((s) => ({ ...s, [n]: s[n] || { size: 'none', qty: 1 } }));
    setNewName('');
    try {
      await axios.post(`${API}/admin/kit/item-types`, { name: n });
      toast.success(`"${n}" saved to item catalog`);
      onAdded(false);
    } catch {
      toast.error('Could not save item to catalog');
    }
  };

  const deleteItemType = async (name) => {
    if (!window.confirm(`Delete "${name}" from the item catalog? Items already issued to staff are NOT affected.`)) return;
    try {
      await axios.delete(`${API}/admin/kit/item-types/${encodeURIComponent(name)}`);
      setCustoms((c) => c.filter((x) => x.toLowerCase() !== name.toLowerCase()));
      setSel((s) => { const n = { ...s }; delete n[name]; return n; });
      toast.success(`"${name}" removed from catalog`);
      onAdded(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete item');
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/kit/items/bulk`, {
        employee_id: employee.employee_id,
        items: Object.entries(sel).map(([name, v]) => ({
          item_name: name,
          size: v.size === 'none' ? null : v.size,
          quantity: Number(v.qty) || 1,
          note: (v.note || '').trim() || null,
        })),
      });
      toast.success(`${count} item(s) issued to ${employee.employee_name}`);
      setSel({}); setCustoms([]); setOpen(false);
      onAdded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue items');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="mt-2 h-8 rounded-sm text-xs uppercase border-primary/30 text-primary hover:bg-primary/10"
        data-testid={`kit-add-items-btn-${employee.employee_id}`}>
        <Package className="w-3 h-3 mr-1" />+ Add items
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSel({}); setCustoms([]); setNewName(''); } }}>
        <DialogContent className="max-w-md h-[85vh] max-h-[85vh] flex flex-col gap-3 overflow-hidden p-4 sm:p-6" data-testid="kit-add-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-sm">Issue kit to {employee.employee_name}</DialogTitle>
            <DialogDescription className="text-xs">Tick everything you hand over — size and quantity per item.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
            data-testid="kit-items-scroll">
            {allItems.map((name) => (
              <div key={name} className="py-1.5 border-b border-border/40 last:border-0">
                <div className="flex items-center gap-2">
                  <Checkbox checked={!!sel[name]} onCheckedChange={() => toggle(name)}
                    id={`kit-chk-${employee.employee_id}-${name}`} data-testid={`kit-check-${name}`} />
                  <label htmlFor={`kit-chk-${employee.employee_id}-${name}`} className="text-sm flex-1 cursor-pointer select-none">
                    {name}
                  </label>
                  {sel[name] && (
                    <>
                      <Select value={sel[name].size} onValueChange={(v) => setField(name, 'size', v)}>
                        <SelectTrigger className="w-20 h-7 text-xs bg-background border-border rounded-sm" data-testid={`kit-bulk-size-${name}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No size</SelectItem>
                          {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input type="number" min="1" value={sel[name].qty}
                        onChange={(e) => setField(name, 'qty', e.target.value)}
                        className="w-14 h-7 text-xs rounded-sm" data-testid={`kit-bulk-qty-${name}`} />
                    </>
                  )}
                  {!sel[name] && canDeleteTypes && (
                    <button
                      type="button"
                      onClick={() => deleteItemType(name)}
                      className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                      title={`Delete "${name}" from catalog`}
                      data-testid={`kit-type-delete-${name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {sel[name] && (
                  <Input value={sel[name].note || ''} onChange={(e) => setField(name, 'note', e.target.value)}
                    placeholder="Note / serial no. (optional)"
                    className="mt-1.5 ml-6 h-7 text-xs rounded-sm w-[calc(100%-1.5rem)]"
                    data-testid={`kit-bulk-note-${name}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1 border-t border-border/60 shrink-0">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustom()}
              placeholder="+ New item type…" className="h-8 text-xs rounded-sm" data-testid="kit-new-item-input" />
            <Button size="sm" variant="outline" onClick={addCustom} disabled={!newName.trim()}
              className="h-8 rounded-sm text-xs" data-testid="kit-new-item-add">
              Add to list
            </Button>
          </div>
          <Button onClick={submit} disabled={count === 0 || busy}
            className="w-full rounded-sm text-xs uppercase shrink-0" data-testid="kit-issue-btn">
            {busy ? 'Issuing…' : `Issue ${count} item${count === 1 ? '' : 's'}`}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmployeeKitRow({ emp, itemTypes, refresh, canDeleteTypes }) {
  const [open, setOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [note, setNote] = useState(emp.note || '');
  const [savingNote, setSavingNote] = useState(false);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await axios.put(`${API}/admin/kit/notes/${emp.employee_id}`, { note });
      toast.success('Note saved');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/admin/kit/items/${deleteItem.id}`);
      toast.success(`${deleteItem.item_name} returned & removed`);
      setDeleteItem(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="border border-border rounded-sm bg-background/50" data-testid={`kit-emp-${emp.employee_id}`}>
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex flex-wrap items-center gap-2 px-3 py-2.5 text-left hover:bg-accent/40 transition-colors"
        data-testid={`kit-emp-toggle-${emp.employee_id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        <span className="text-sm font-medium">{emp.employee_name}</span>
        {emp.card_code ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-mono">
            <QrCode className="w-3 h-3" />{emp.card_code}
          </span>
        ) : (
          <span className="text-[10px] uppercase text-amber-500 border border-amber-500/40 rounded-sm px-1">no QR card</span>
        )}
        <span className="text-xs text-muted-foreground truncate">
          {emp.items.length > 0 ? emp.items.map(itemLabel).join(' · ') : 'No kit issued'}
        </span>
        {emp.note && (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-500 truncate max-w-[220px]" data-testid={`kit-note-preview-${emp.employee_id}`}>
            <StickyNote className="w-3 h-3 shrink-0" />{emp.note}
          </span>
        )}
        <Badge variant="outline" className="ml-auto text-[10px] rounded-sm">{emp.items.length} items</Badge>
      </button>
      {open && (
        <div className="px-3 pb-3 pl-9">
          {emp.items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-1">Nothing issued yet.</p>
          ) : (
            <div className="space-y-1">
              {emp.items.map((it) => (
                <div key={it.id} className="flex flex-wrap items-center gap-2 text-xs py-1 border-b border-border/40 last:border-0"
                  data-testid={`kit-item-${it.id}`}>
                  <Package className="w-3 h-3 text-primary shrink-0" />
                  <span className="font-medium">{itemLabel(it)}</span>
                  {it.note && <span className="text-muted-foreground">— {it.note}</span>}
                  <span className="text-muted-foreground ml-auto">
                    {it.issued_at ? format(parseISO(it.issued_at), 'dd MMM yyyy') : ''}
                    {it.issued_by_name ? ` · by ${it.issued_by_name}` : ''}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteItem(it)}
                    className="h-6 px-2 rounded-sm text-[10px] uppercase text-red-400 hover:bg-red-500/10"
                    data-testid={`kit-return-${it.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" />Returned
                  </Button>
                </div>
              ))}
            </div>
          )}
          <AddItemsDialog employee={emp} itemTypes={itemTypes} onAdded={refresh} canDeleteTypes={canDeleteTypes} />
          <div className="mt-3 pt-2 border-t border-border/60 space-y-1.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <StickyNote className="w-3 h-3" />Notes
              {emp.note_updated_by && (
                <span className="normal-case tracking-normal">— last by {emp.note_updated_by}{emp.note_updated_at ? ` · ${format(parseISO(emp.note_updated_at), 'dd MMM yyyy HH:mm')}` : ''}</span>
              )}
            </p>
            <div className="flex items-start gap-2">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="e.g. Tent shared with John S."
                className="text-xs rounded-sm bg-background border-border min-h-[52px]"
                data-testid={`kit-note-input-${emp.employee_id}`} />
              <Button size="sm" onClick={saveNote} disabled={savingNote || note === (emp.note || '')}
                className="h-8 rounded-sm text-xs uppercase shrink-0"
                data-testid={`kit-note-save-${emp.employee_id}`}>
                {savingNote ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Item returned?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteItem && itemLabel(deleteItem)}" will be removed from {emp.employee_name}'s kit list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="kit-return-confirm">Yes, returned</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function KitPage() {
  const { user } = useAuth();
  const Layout = user?.role === 'supervisor' ? SupervisorLayout : AdminLayout;
  const canDeleteTypes = user?.role === 'admin';
  const [data, setData] = useState({ events: [], no_event: [] });
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [staffRes, typesRes] = await Promise.all([
        axios.get(`${API}/admin/kit/staff`),
        axios.get(`${API}/admin/kit/item-types`),
      ]);
      setData(staffRes.data);
      setItemTypes(typesRes.data.items || []);
    } catch {
      toast.error('Failed to load kit data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useAutoRefresh(fetchAll, { match: ['/kit'] });

  const empty = data.events.length === 0 && data.no_event.length === 0;

  const q = search.trim().toLowerCase();
  const matches = (emp) =>
    !q ||
    (emp.employee_name || '').toLowerCase().includes(q) ||
    (emp.items || []).some((it) => (it.item_name || '').toLowerCase().includes(q));
  const filteredEvents = data.events
    .map((ev) => ({ ...ev, employees: ev.employees.filter(matches) }))
    .filter((ev) => ev.employees.length > 0);
  const filteredNoEvent = data.no_event.filter(matches);
  const noResults = q && filteredEvents.length === 0 && filteredNoEvent.length === 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="kit-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase">Kit</h1>
          <p className="text-sm text-muted-foreground">
            Uniform & equipment for staff holding a QR card, grouped by upcoming event.
          </p>
        </div>

        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by staff name or item…"
            className="pl-9 rounded-sm bg-background border-border text-sm"
            data-testid="kit-search-input"
          />
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : empty ? (
          <Card className="border-border"><CardContent className="py-10 text-center text-sm text-muted-foreground">
            No staff with an assigned QR card. Assign cards from Feedback → QR Cards first.
          </CardContent></Card>
        ) : noResults ? (
          <Card className="border-border"><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="kit-search-empty">
            No staff or items matching "{search}".
          </CardContent></Card>
        ) : (
          <div className="space-y-6">
            {filteredEvents.map((ev) => (
              <div key={ev.location} className="space-y-2" data-testid={`kit-event-${ev.location}`}>
                <h2 className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />{ev.location}
                  <span className="text-xs font-normal text-muted-foreground">({ev.employees.length} staff)</span>
                </h2>
                <div className="space-y-2">
                  {ev.employees.map((emp) => (
                    <EmployeeKitRow key={emp.employee_id} emp={emp} itemTypes={itemTypes} refresh={fetchAll} canDeleteTypes={canDeleteTypes} />
                  ))}
                </div>
              </div>
            ))}
            {filteredNoEvent.length > 0 && (
              <div className="space-y-2" data-testid="kit-no-event-group">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Other staff with kit or QR (no upcoming event)
                </h2>
                <div className="space-y-2">
                  {filteredNoEvent.map((emp) => (
                    <EmployeeKitRow key={emp.employee_id} emp={emp} itemTypes={itemTypes} refresh={fetchAll} canDeleteTypes={canDeleteTypes} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
